import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Clock, XCircle, Loader2, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mapování festivalů a jejich kontaktních údajů
const FESTIVAL_CONFIG = {
  'kontakt-fest-2026': {
    name: 'KONTAKT Fest 2026',
    contact: {
      name: 'Monika Ondruchová',
      email: 'monika@vseproakci.cz',
      phone: '+420 773 550 880'
    }
  },
  'cesta-kolem-sveta-2026': {
    name: 'Gastrofestival Cesta Kolem Světa 2026',
    contact: {
      name: 'Monika Ondruchová',
      email: 'monika@vseproakci.cz',
      phone: '+420 773 550 880'
    }
  },
  'otrokovicke-letni-slavnosti-2026': {
    name: 'Otrokovické letní slavnosti 2026',
    contact: {
      name: 'Jakub Vodárek',
      email: 'info@vseproakci.cz',
      phone: '+420 774 750 456'
    }
  },
  'festival-koupacek-2026': {
    name: 'Festival Koupáček 2026',
    contact: {
      name: 'Jakub Vodárek',
      email: 'info@vseproakci.cz',
      phone: '+420 774 750 456'
    }
  },
  'burger-rock-fest-2026': {
    name: 'BURGER ROCK FEST NAPAJEDLA 2026',
    contact: {
      name: 'Jakub Vodárek',
      email: 'info@vseproakci.cz',
      phone: '+420 774 750 456'
    }
  },
  'majofest-otrokovice-2026': {
    name: 'MÁJOFEST OTROKOVICE 2026',
    contact: {
      name: 'Jakub Vodárek',
      email: 'info@vseproakci.cz',
      phone: '+420 774 750 456'
    }
  }
};

const ApplicationStatus = () => {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadApplication();
  }, [id]);

  const loadApplication = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendor-applications/status/${id}`);
      setApplication(response.data);
    } catch (error) {
      setError('Přihláška nebyla nalezena');
      console.error('Error loading application:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Získat konfiguraci festivalu podle eventId
  const getFestivalConfig = () => {
    if (!application || !application.eventId) {
      return FESTIVAL_CONFIG['kontakt-fest-2026']; // fallback
    }
    return FESTIVAL_CONFIG[application.eventId] || FESTIVAL_CONFIG['kontakt-fest-2026'];
  };

  const getStatusDisplay = () => {
    if (!application) return null;

    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        title: 'Přihláška čeká na schválení',
        message: 'Vaše přihláška byla úspěšně odeslána a čeká na schválení organizátorem. O výsledku Vás budeme informovat.'
      },
      approved: {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        title: 'Přihláška schválena! 🎉',
        message: null
      },
      rejected: {
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        title: 'Přihláška nebyla schválena',
        message: 'Bohužel Vaše přihláška nebyla tentokrát schválena. Pro více informací nás prosím kontaktujte.'
      }
    };

    const config = statusConfig[application.status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <div className={`${config.bgColor} ${config.borderColor} border-2 rounded-lg p-6 mb-6`}>
        <div className="flex items-center gap-3 mb-4">
          <Icon className={`w-8 h-8 ${config.color}`} />
          <h2 className={`text-2xl font-bold ${config.color}`}>{config.title}</h2>
        </div>
        {config.message && (
          <p className="text-gray-700 mb-4">{config.message}</p>
        )}

        {application.status === 'approved' && (
          <div className="mt-4 space-y-4">
            <div className="bg-white p-4 rounded-lg border border-green-300">
              <p className="text-gray-800 mb-3">
                S radostí Vám oznamujeme, že <strong>Vaše přihláška na {getFestivalConfig().name} byla schválena!</strong>
              </p>
              <p className="text-gray-700 mb-3">
                V nejbližší době se na této kartě objeví zálohová faktura. 
                Po úhradě faktury s Vámi závazně počítáme jako s účastníkem festivalu.
              </p>
              
              {/* Důležité termíny - POUZE pokud ještě není zaplaceno */}
              {application.paymentStatus !== 'paid' && (
                <>
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-4 my-4">
                    <p className="font-bold text-orange-900 mb-2">⚠️ Důležité termíny:</p>
                    <ul className="list-disc list-inside text-orange-800 space-y-1">
                      <li><strong>Splatnost faktury:</strong> nejpozději 6. 4. 2026</li>
                      <li>V případě neuhrazení do tohoto data bude Vaše registrace považována za neplatnou</li>
                    </ul>
                  </div>

                  {application.totalPrice && (
                    <div className="bg-blue-50 p-3 rounded border border-blue-200 mb-3">
                      <p className="text-sm text-blue-900">
                        <strong>Částka k úhradě:</strong> {application.totalPrice.toLocaleString('cs-CZ')} Kč bez DPH
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Potvrzení platby - zobrazí se pouze když je zaplaceno */}
              {application.paymentStatus === 'paid' && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-green-900 text-lg mb-2">✅ Platba přijata!</p>
                      <p className="text-green-800 mb-2">
                        Potvrzujeme přijetí platby na náš účet. <strong>Tímto s vámi na festivalu počítáme!</strong>
                      </p>
                      <p className="text-green-700 text-sm">
                        💡 Více informací zveřejníme cca týden před akcí na této kartě.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-gray-700 mt-3">
                Těšíme se na spolupráci!
              </p>
            </div>

            {/* Dokumenty k stažení */}
            {application.documents && Object.keys(application.documents).length > 0 && (
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 mt-4">
                <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  📄 Dokumenty k stažení
                </h5>
                <p className="text-sm text-purple-800 mb-3">
                  Zde najdete všechny dokumenty související s Vaší přihláškou:
                </p>
                <div className="space-y-2">
                  {application.documents.zalohova_faktura && (
                    <a
                      href={`${BACKEND_URL}${application.documents.zalohova_faktura.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between bg-white p-3 rounded border border-purple-200 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-semibold text-sm">Zálohová faktura</p>
                          <p className="text-xs text-gray-500">{application.documents.zalohova_faktura.filename}</p>
                        </div>
                      </div>
                      <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white">
                        <Download className="w-4 h-4 mr-1" />
                        Stáhnout
                      </Button>
                    </a>
                  )}
                  {application.documents.danovy_doklad && (
                    <a
                      href={`${BACKEND_URL}${application.documents.danovy_doklad.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between bg-white p-3 rounded border border-purple-200 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-semibold text-sm">Daňový doklad</p>
                          <p className="text-xs text-gray-500">{application.documents.danovy_doklad.filename}</p>
                        </div>
                      </div>
                      <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white">
                        <Download className="w-4 h-4 mr-1" />
                        Stáhnout
                      </Button>
                    </a>
                  )}
                  {application.documents.faktura && (
                    <a
                      href={`${BACKEND_URL}${application.documents.faktura.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between bg-white p-3 rounded border border-purple-200 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-semibold text-sm">Faktura</p>
                          <p className="text-xs text-gray-500">{application.documents.faktura.filename}</p>
                        </div>
                      </div>
                      <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white">
                        <Download className="w-4 h-4 mr-1" />
                        Stáhnout
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Načítám stav přihlášky...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2 border-red-300">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-600 mb-2">Chyba</h2>
            <p className="text-gray-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">{getFestivalConfig().name}</h1>
          <p className="text-gray-600">Stav Vaší přihlášky</p>
        </div>

        <Card className="border-2 border-orange-200 mb-6">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
            <CardTitle className="text-2xl">
              Informace o přihlášce
              <div className="text-3xl font-bold mt-2">{application.vendorName}</div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {getStatusDisplay()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Kontaktní osoba</p>
                <p className="font-bold text-gray-900">{application.name}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Email</p>
                <p className="font-bold text-gray-900">{application.email}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Telefon</p>
                <p className="font-bold text-gray-900">{application.phone}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Datum podání</p>
                <p className="font-bold text-gray-900">
                  {new Date(application.createdAt).toLocaleDateString('cs-CZ')}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-semibold text-slate-900 mb-2">📞 Kontakt na organizátora:</p>
              <p className="text-sm text-slate-700">
                <strong>{getFestivalConfig().contact.name}</strong><br />
                Email: {getFestivalConfig().contact.email}<br />
                Tel: {getFestivalConfig().contact.phone}
              </p>
              <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-slate-200">
                Registrační číslo: <span className="font-mono font-semibold">{application.id}</span>
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-400">
                💡 Tento link si uložte - můžete se k němu kdykoliv vrátit a zkontrolovat stav přihlášky
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationStatus;
